/**
 * Formatting for the German UI. Amounts are cents (bigint-safe numbers), areas are m² with one decimal.
 * Every function is pure and locale-fixed to de-DE so that server-rendered PDFs and the SPA agree.
 */
const LOCALE = 'de-DE';
const eurWhole = new Intl.NumberFormat(LOCALE, {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
});
const eurCents = new Intl.NumberFormat(LOCALE, {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
});
const number0 = new Intl.NumberFormat(LOCALE, { maximumFractionDigits: 0 });
const number1 = new Intl.NumberFormat(LOCALE, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const number2 = new Intl.NumberFormat(LOCALE, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
/** Intl inserts a narrow no-break space (U+202F) before units; the screens use an ordinary non-breaking space. */
const NBSP = '\u00a0';
function unitSpace(text) {
    return text.replaceAll('\u202f', NBSP);
}
/** 49800000 → "498.000 €". Cents below a full euro are dropped only when the amount is a whole euro. */
export function formatEuro(cents) {
    const value = Number(cents) / 100;
    return unitSpace(Number.isInteger(value) ? eurWhole.format(value) : eurCents.format(value));
}
/** Always with two decimals: 1234500 → "12.345,00 €". */
export function formatEuroExact(cents) {
    return unitSpace(eurCents.format(Number(cents) / 100));
}
/** 98.4 → "98,4 m²". */
export function formatSquareMeters(sqm) {
    return `${number1.format(sqm)}${NBSP}m²`;
}
/** 3.57 → "3,57 %"; 6 → "6 %". */
export function formatPercent(pct, decimals = 2) {
    const formatter = decimals === 0 ? number0 : decimals === 1 ? number1 : number2;
    const trimmed = Number.isInteger(pct) ? number0.format(pct) : formatter.format(pct);
    return `${trimmed}${NBSP}%`;
}
export function formatInteger(value) {
    return number0.format(value);
}
const dateShort = new Intl.DateTimeFormat(LOCALE, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'Europe/Berlin',
});
const dateWeekday = new Intl.DateTimeFormat(LOCALE, {
    weekday: 'short',
    day: '2-digit',
    month: '2-digit',
    timeZone: 'Europe/Berlin',
});
const timeShort = new Intl.DateTimeFormat(LOCALE, {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Berlin',
});
const dateLong = new Intl.DateTimeFormat(LOCALE, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'Europe/Berlin',
});
/** "26.09.2026" */
export function formatDate(value) {
    return dateShort.format(toDate(value));
}
/** "Fr., 26.09." */
export function formatWeekdayDate(value) {
    return dateWeekday.format(toDate(value));
}
/** "14:30" */
export function formatTime(value) {
    return timeShort.format(toDate(value));
}
/** "26.09.2026 · 14:30" */
export function formatDateTime(value) {
    return `${formatDate(value)} · ${formatTime(value)}`;
}
/** "26. September 2026" */
export function formatDateLong(value) {
    return dateLong.format(toDate(value));
}
/** ISO calendar day (YYYY-MM-DD) in Europe/Berlin for a point in time. */
export function toBerlinDateKey(value) {
    const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Europe/Berlin',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).formatToParts(toDate(value));
    const get = (type) => parts.find((p) => p.type === type)?.value ?? '';
    return `${get('year')}-${get('month')}-${get('day')}`;
}
function dayNumber(key) {
    const [year, month, day] = key.split('-').map(Number);
    return Math.round(Date.UTC(year ?? 1970, (month ?? 1) - 1, day ?? 1) / 86_400_000);
}
/**
 * Age of a point in time in Berlin calendar days, as the lists show it under a date: „heute", „gestern",
 * „vor 3 Tagen" (2–6), „vor 1 Woche"/„vor 2 Wochen" (7–29), „vor 1 Monat"/„vor 3 Monaten" (30–364, 30 days a
 * month), „vor 1 Jahr"/„vor 2 Jahren". A point in the future counts as „heute".
 */
export function formatRelativeDays(value, now = new Date()) {
    const days = Math.max(dayNumber(toBerlinDateKey(now)) - dayNumber(toBerlinDateKey(value)), 0);
    if (days === 0)
        return 'heute';
    if (days === 1)
        return 'gestern';
    if (days < 7)
        return `vor ${days} Tagen`;
    if (days < 30) {
        const weeks = Math.floor(days / 7);
        return weeks === 1 ? 'vor 1 Woche' : `vor ${weeks} Wochen`;
    }
    if (days < 365) {
        const months = Math.floor(days / 30);
        return months === 1 ? 'vor 1 Monat' : `vor ${months} Monaten`;
    }
    const years = Math.floor(days / 365);
    return years === 1 ? 'vor 1 Jahr' : `vor ${years} Jahren`;
}
function toDate(value) {
    return value instanceof Date ? value : new Date(value);
}
/** "J. Weiler" from first and last name. */
export function formatShortName(firstName, lastName) {
    const initial = firstName.trim().charAt(0);
    return initial ? `${initial}. ${lastName.trim()}` : lastName.trim();
}
/** Initials for avatars: "Julia Weiler" → "JW". */
export function formatInitials(firstName, lastName) {
    return `${firstName.trim().charAt(0)}${lastName.trim().charAt(0)}`.toUpperCase();
}
/** Editing text of an amount field: 123450 → "1.234,50" (no currency sign; the field shows "€" as suffix). */
export function formatAmountInput(cents) {
    return number2.format(Number(cents) / 100);
}
/**
 * What a user typed into an amount field → cents: "1.234,50", "1234,5", "1.234" (thousands) and "1234.50" are accepted;
 * empty or unreadable input gives null.
 */
export function parseAmountInput(text) {
    const compact = text.replace(/[\s\u00a0\u202f\u20ac]/g, '');
    if (!compact)
        return null;
    let normalized;
    if (compact.includes(','))
        normalized = compact.replaceAll('.', '').replace(',', '.');
    else if (/^-?\d{1,3}(\.\d{3})+$/.test(compact))
        normalized = compact.replaceAll('.', '');
    else
        normalized = compact;
    if (!/^-?\d+(\.\d{1,2})?$/.test(normalized))
        return null;
    return Math.round(Number(normalized) * 100);
}
const percentInput = new Intl.NumberFormat(LOCALE, { maximumFractionDigits: 3, useGrouping: false });
/** Editing text of a percentage field: 3.5 → "3,5", 45 → "45", 0.125 → "0,125" (the field shows "%" as suffix). */
export function formatPercentInput(pct) {
    return percentInput.format(pct);
}
/**
 * What a user typed into a percentage field → the number: "3,5", "3.5", "3,5 %" and ",5" are accepted with at most
 * three decimals (`numeric(6,3)`); empty or unreadable input gives null. Ranges are the caller's schema's concern.
 */
export function parsePercentInput(text) {
    const compact = text.replace(/[\s\u00a0\u202f%]/g, '');
    if (!compact)
        return null;
    const normalized = compact.replace(',', '.');
    if (!/^-?(\d+(\.\d{1,3})?|\.\d{1,3})$/.test(normalized))
        return null;
    return Number(normalized);
}
//# sourceMappingURL=format.js.map